import React, { BaseSyntheticEvent, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { FieldValues, useForm } from "react-hook-form";
import { useTheme } from "@mui/material";
import { Spinner } from "components/common/spinner/spinner.component";
import CustomTable from "components/table/table.component";
import { CommonSelect } from "components/common/common-select/common-select.component";
import { Popup } from "components/common/popup/popup.component";
import { getCurrentPage } from "utils/util-functions";
import { FieldError } from "components/common/Input";
import { HeaderTitle } from "components/common/Text";
import { DetailWrapper, PageSection, PageHeader } from "components/common/Wrapper";
import { NxtIconButton } from "components/common/nxt-icon-button/nxt-icon-button.component";
import { TableColumnTypes } from "pages/configs/configs";
import { showError, showSuccess, showWarning } from "utils/notifications";
import { bulkUpdateDeviceSoftware, bulkPushDeviceConfig, updateDevice } from "api/DeviceHelpers";
import { TableBodyCell } from "components/table/table.style";
import { useRequest } from "hooks/useRequest";
import POOL_DEVICE_COLS from "./pool-detail.config";
import { DevicePoolTypes, PoolDeviceTypes } from "../pools.config";
import { StatusDot } from "pages/Devices/Detail/components/form-component/DeviceDetail.style";
import { getTableCellValue } from "pages/Devices/utilities/device.utilities";
import { DeviceColumnTypes } from "pages/Devices/devices.config";
import { UpdateSoftwareDialog } from "components/common/update-software-dialog/update-software-dialog.component";
import { PushConfigDialog } from "components/common/push-config-dialog/push-config-dialog.component";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import style from "./pool-detail.module.css";

const PoolDetail = (): JSX.Element => {
  const { poolId } = useParams();
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentRow, setCurrentRow] = useState<PoolDeviceTypes | null>(null);
  const [isTransforming, setIsTransforming] = useState<boolean>(false);
  const open = Boolean(anchorEl);
  const [isOpenTransferModal, setIsOpenTransferModal] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isOpenUpdateDialog, setIsOpenUpdateDialog] = useState<boolean>(false);
  const [isUpdatingSoftware, setIsUpdatingSoftware] = useState<boolean>(false);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState<HTMLElement | null>(null);
  const optionsMenuOpen = Boolean(optionsAnchorEl);
  const [isOpenConfigDialog, setIsOpenConfigDialog] = useState<boolean>(false);
  const [isPushingConfig, setIsPushingConfig] = useState<boolean>(false);
  const showConfigPush = useFeatureFlag("configPush");
  const navigate = useNavigate();

  const theme = useTheme();
  const { data, isLoading, mutate } = useRequest(`devices?devicePool=${poolId}`);
  const totalDeviceCount = useMemo(() => data?.length || 0, [data]);
  const deviceList: PoolDeviceTypes[] = useMemo(
    () =>
      data?.map((device: any) => ({
        ...device,
        eiSoftwareVersion: device.softwareVersion || device.eiSoftwareVersion,
      })) || [],
    [data],
  );

  const paginatedList = useMemo(() => getCurrentPage(deviceList, page, rowsPerPage), [deviceList, page, rowsPerPage]);

  const { data: poolsData } = useRequest("devicePools");

  const poolList: DevicePoolTypes[] = useMemo(
    () => (poolsData?.length > 0 ? poolsData.filter(({ devicePool }: DevicePoolTypes) => devicePool !== poolId) : []),
    [poolsData, poolId],
  );
  const poolOptions = useMemo(
    () => poolList.map(({ devicePool }: DevicePoolTypes) => ({ value: devicePool, label: devicePool })),
    [poolList],
  );

  const bodyColumns = useMemo(() => {
    const tempCols = [...POOL_DEVICE_COLS];
    tempCols.pop();
    return tempCols;
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, row: PoolDeviceTypes) => {
    const { serialNumber } = row;

    if (!serialNumber) {
      return;
    }

    const newSelected = [...selectedRows];
    const index = selectedRows.indexOf(serialNumber);

    if (event.target.checked) {
      if (index === -1) {
        newSelected.push(serialNumber);
      }
    } else if (index !== -1) {
      newSelected.splice(index, 1);
    }

    setSelectedRows(newSelected);
  };

  const isSelectedRow = (serialNumber: string | undefined) => {
    if (!serialNumber) {
      return false;
    }
    return selectedRows.indexOf(serialNumber) !== -1;
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allSerialNumbers = deviceList.map((row: PoolDeviceTypes) => row.serialNumber).filter(Boolean) as string[];
      setSelectedRows(allSerialNumbers);
    } else {
      setSelectedRows([]);
    }
  };

  const isAllSelected = useMemo(() => {
    if (deviceList.length === 0) return false;
    const allSerialNumbers = deviceList.map((row: PoolDeviceTypes) => row.serialNumber).filter(Boolean) as string[];
    return (
      allSerialNumbers.length > 0 && allSerialNumbers.every((serialNumber) => selectedRows.indexOf(serialNumber) !== -1)
    );
  }, [deviceList, selectedRows]);

  const getCheckBox = (isItemSelected: boolean | undefined, row: PoolDeviceTypes, isHeader = false) => {
    if (isHeader) {
      return (
        <TableBodyCell padding="checkbox">
          <Checkbox
            color="primary"
            checked={isAllSelected}
            indeterminate={selectedRows.length > 0 && !isAllSelected}
            onChange={handleSelectAll}
          />
        </TableBodyCell>
      );
    }

    return (
      <TableBodyCell padding="checkbox">
        <Checkbox color="primary" checked={isItemSelected} onChange={(event) => handleChange(event, row)} />
      </TableBodyCell>
    );
  };

  const {
    handleSubmit,
    formState: { errors },
    control,
  }: FieldValues = useForm({ values: { devicePool: poolList[0]?.devicePool! } });

  const onClickMenu = (event: BaseSyntheticEvent, row: PoolDeviceTypes) => {
    setAnchorEl(event.currentTarget);
    setCurrentRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentRow(null);
  };

  const transferDevice = () => {
    setAnchorEl(null);
    setIsOpenTransferModal(true);
  };

  const onModalClose = (event: never, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason !== "backdropClick") {
      setIsOpenTransferModal(false);
    }
  };

  const onConfirmTransfer = async ({ devicePool }: FieldValues): Promise<void> => {
    if (!devicePool) {
      showError("Device pool is not valid!");
      return;
    }

    if (!currentRow) {
      return;
    }

    setIsTransforming(true);

    try {
      // call PUT /devices API
      const result = await updateDevice({
        serialNumber: currentRow.serialNumber,
        devicePool,
      });

      if (result.data) {
        // close modal & show alert
        setIsOpenTransferModal(false);
        showSuccess(`Success to transfer a device ${currentRow.serialNumber} to ${devicePool} pool!`);

        // Refresh table
        mutate();
      }
    } catch (error) {
      // error handling
    } finally {
      setIsTransforming(false);
    }
  };

  const onClickSN = (serialNumber: string) => {
    navigate(`/devices/${serialNumber}`);
  };

  const handleOptionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setOptionsAnchorEl(event.currentTarget);
  };

  const handleOptionsClose = () => {
    setOptionsAnchorEl(null);
  };

  const handleUpdateDeviceSoftware = () => {
    handleOptionsClose();
    handleClose();

    // Filter selected devices to only those with updateAvailable: true
    const devicesWithUpdates = paginatedList.filter(
      (device: PoolDeviceTypes) =>
        device.serialNumber && selectedRows.includes(device.serialNumber) && device.updateAvailable === true,
    );

    if (devicesWithUpdates.length === 0) {
      showWarning("No selected devices have updates available.");
      return;
    }

    setIsOpenUpdateDialog(true);
  };

  const handleConfirmUpdate = async (updateNow: boolean, updateTime: string) => {
    // Filter selected devices to only those with updateAvailable: true
    const devicesWithUpdates = paginatedList.filter(
      (device: PoolDeviceTypes) =>
        device.serialNumber && selectedRows.includes(device.serialNumber) && device.updateAvailable === true,
    );

    const serialNumbers = devicesWithUpdates
      .map((device: PoolDeviceTypes) => device.serialNumber)
      .filter(Boolean) as string[];

    if (serialNumbers.length === 0) {
      showWarning("No devices with updates available.");
      setIsOpenUpdateDialog(false);
      return;
    }

    setIsUpdatingSoftware(true);

    try {
      const response = await bulkUpdateDeviceSoftware({
        serialNumbers,
        updateNow,
        updateTime,
      });

      if (response?.data === "Success") {
        showSuccess(
          `Software update ${updateNow ? "initiated" : "scheduled"} successfully for ${
            serialNumbers.length
          } device(s).`,
        );
        setIsOpenUpdateDialog(false);
        setSelectedRows([]);
        mutate();
      } else if (response?.data?.includes("Rejected")) {
        showError(response.data);
        setIsOpenUpdateDialog(false);
      } else {
        showSuccess(`Software update ${updateNow ? "initiated" : "scheduled"} successfully.`);
        setIsOpenUpdateDialog(false);
        setSelectedRows([]);
        mutate();
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data || error?.message || "Failed to update device software.";
      showError(errorMessage);
    } finally {
      setIsUpdatingSoftware(false);
    }
  };

  const getMegaFi2SerialNumbers = (): string[] => {
    return paginatedList
      .filter(
        (device: PoolDeviceTypes) =>
          device.serialNumber &&
          selectedRows.includes(device.serialNumber) &&
          device.eiSoftwareVersion?.startsWith("MegaFi_2"),
      )
      .map((device: PoolDeviceTypes) => device.serialNumber)
      .filter(Boolean) as string[];
  };

  const handlePushDeviceConfig = () => {
    handleOptionsClose();
    handleClose();

    const megaFi2Devices = getMegaFi2SerialNumbers();

    if (megaFi2Devices.length === 0) {
      showWarning("Configuration push is only supported on MegaFi 2 devices.");
      return;
    }

    setIsOpenConfigDialog(true);
  };

  const handleConfirmConfigPush = async (
    configFileBase64: string,
    configFileName: string,
    applyNow: boolean,
    scheduleTime: string,
  ) => {
    const serialNumbers = getMegaFi2SerialNumbers();

    if (serialNumbers.length === 0) {
      showWarning("No MegaFi 2 devices selected.");
      setIsOpenConfigDialog(false);
      return;
    }

    setIsPushingConfig(true);

    try {
      const response = await bulkPushDeviceConfig({
        serialNumbers,
        configFileBase64,
        configFileName,
        applyNow,
        scheduleTime,
      });

      if (response?.data?.includes?.("Rejected")) {
        showError(response.data);
        setIsOpenConfigDialog(false);
      } else {
        showSuccess(
          `Configuration push ${applyNow ? "initiated" : "scheduled"} for ${serialNumbers.length} device(s).`,
        );
        setIsOpenConfigDialog(false);
        setSelectedRows([]);
        mutate();
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data || error?.message || "Failed to push device configuration.";
      showError(errorMessage);
    } finally {
      setIsPushingConfig(false);
    }
  };

  return (
    <DetailWrapper>
      <PageSection>
        <PageHeader>
          <HeaderTitle>{`Devices in Pool '${poolId}'`}</HeaderTitle>
          {selectedRows.length > 0 && (
            <NxtIconButton
              id="pool-options-button"
              aria-controls={optionsMenuOpen ? "pool-options-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={optionsMenuOpen ? "true" : undefined}
              icon={<MoreVertIcon />}
              onClick={handleOptionsClick}
              text="Options"
            />
          )}
        </PageHeader>

        <CustomTable
          rowsPerPageOptions={[10, 25, 100]}
          totalCount={totalDeviceCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(pageNum) => setPage(pageNum)}
          onRowsPerPageChange={(rowPerPage) => setRowsPerPage(rowPerPage)}
        >
          <CustomTable.Header
            columns={POOL_DEVICE_COLS}
            onSelectAll={handleSelectAll}
            isAllSelected={isAllSelected}
            hasCheckbox
            isIndeterminate={selectedRows.length > 0 && !isAllSelected}
          />

          <CustomTable.Body isLoading={isLoading} deviceData={paginatedList}>
            {paginatedList &&
              paginatedList.map((row: PoolDeviceTypes) => {
                const isItemSelected = isSelectedRow(row.serialNumber);
                return (
                  <TableRow hover key={row.serialNumber} selected={isItemSelected}>
                    {getCheckBox(isItemSelected, row)}
                    {bodyColumns.map((column: TableColumnTypes) => {
                      const value = row[column.id as keyof PoolDeviceTypes];

                      // Skip rendering checkbox column again
                      if (column.id === "") {
                        return null;
                      }

                      // Use getTableCellValue for updateAvailable column
                      if (column.id === "updateAvailable") {
                        const columnConfig: DeviceColumnTypes = {
                          id: "updateAvailable",
                          label: "Update Available",
                          align: "left",
                        };
                        return (
                          <TableBodyCell key={column.id} align="left" onClick={() => onClickSN(row.serialNumber)}>
                            {getTableCellValue(columnConfig, value, row)}
                          </TableBodyCell>
                        );
                      }

                      return (
                        <TableBodyCell key={column.id} onClick={() => onClickSN(row.serialNumber)}>
                          {column.id === "online" && value ? (
                            <StatusDot className={style.statusDot} />
                          ) : (
                            <span>{value}</span>
                          )}
                        </TableBodyCell>
                      );
                    })}

                    <TableBodyCell style={{ width: 30 }}>
                      <Button onClick={(e) => onClickMenu(e, row)} style={{ color: theme.palette.common.black }}>
                        <MoreVertIcon />
                      </Button>
                    </TableBodyCell>
                  </TableRow>
                );
              })}
          </CustomTable.Body>
        </CustomTable>
      </PageSection>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={transferDevice}>Transfer</MenuItem>
        <MenuItem onClick={handleUpdateDeviceSoftware}>Update Device Software</MenuItem>
        {showConfigPush && <MenuItem onClick={handlePushDeviceConfig}>Push Device Configuration</MenuItem>}
      </Menu>

      <Menu
        id="pool-options-menu"
        aria-labelledby="pool-options-button"
        anchorEl={optionsAnchorEl}
        open={optionsMenuOpen}
        onClose={handleOptionsClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={handleUpdateDeviceSoftware}>Update Device Software</MenuItem>
        {showConfigPush && <MenuItem onClick={handlePushDeviceConfig}>Push Device Configuration</MenuItem>}
      </Menu>

      <Popup
        title="Transfer Device"
        open={isOpenTransferModal}
        onClose={onModalClose}
        primaryBtn={{ text: "Save", onClick: handleSubmit(onConfirmTransfer), disabled: isTransforming }}
        secondaryBtn={{ text: "Cancel", onClick: () => setIsOpenTransferModal(false) }}
      >
        <div
          style={{ paddingBottom: "8px" }}
        >{`Please select a pool to transfer device '${currentRow?.serialNumber}'.`}</div>

        <form>
          <CommonSelect
            width="100%"
            name="devicePool"
            options={poolOptions}
            control={control}
            rules={{ required: "Device Pool is required" }}
            error={errors.devicePool}
          />
          {errors.devicePool && <FieldError>{errors.devicePool.message}</FieldError>}
        </form>
      </Popup>

      <Popup title="Transfering Device..." open={isTransforming}>
        <Spinner />
      </Popup>

      <UpdateSoftwareDialog
        open={isOpenUpdateDialog}
        onClose={() => setIsOpenUpdateDialog(false)}
        deviceCount={
          paginatedList.filter(
            (device: PoolDeviceTypes) =>
              device.serialNumber && selectedRows.includes(device.serialNumber) && device.updateAvailable === true,
          ).length
        }
        onConfirm={handleConfirmUpdate}
      />

      <PushConfigDialog
        open={isOpenConfigDialog}
        onClose={() => setIsOpenConfigDialog(false)}
        deviceCount={getMegaFi2SerialNumbers().length}
        isSubmitting={isPushingConfig}
        onConfirm={handleConfirmConfigPush}
      />
    </DetailWrapper>
  );
};

export default PoolDetail;
